import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { getInvoices, uploadBilty, replaceBilty, getSettings, getDocuments, uploadDocument, deleteDocument } from '../lib/api';
import { formatDate, getBiltyStatusColor } from '../lib/utils';
import { Search, Upload, FileText, RefreshCw, Check, AlertCircle, FolderOpen, Trash2, Download, File } from 'lucide-react';

function BiltySection() {
  const [invoices, setInvoices] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);
  const [message, setMessage] = useState(null);
  const [biltyApplicableFrom, setBiltyApplicableFrom] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [invRes, settingsRes] = await Promise.all([
        getInvoices({ limit: 50, search, sort_by: 'bill_date', sort_order: 'desc' }),
        getSettings(),
      ]);
      setInvoices(invRes.data.invoices || []);
      setBiltyApplicableFrom(settingsRes.data?.bilty_applicable_from);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [search]);

  const isBiltyEligible = (inv) => {
    if (!biltyApplicableFrom) return true;
    if (!inv.bill_date) return false;
    return new Date(inv.bill_date) >= new Date(biltyApplicableFrom);
  };

  const handleUpload = async (invoiceId, file, isReplace = false) => {
    setUploading(invoiceId);
    setMessage(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (isReplace) {
        await replaceBilty(invoiceId, fd);
      } else {
        await uploadBilty(invoiceId, fd);
      }
      setMessage({ type: 'success', text: 'Bilty uploaded successfully!' });
      load();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Upload failed' });
    }
    setUploading(null);
  };

  return (
    <div className="space-y-4" data-testid="bilty-page">
      {biltyApplicableFrom && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-sm text-blue-800 flex items-center gap-2">
          <AlertCircle size={16} /> Bilty upload is only available for invoices from {formatDate(biltyApplicableFrom)} onwards
        </div>
      )}

      {message && (
        <div className={`px-4 py-3 rounded-xl text-sm flex items-center gap-2 ${message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
          {message.type === 'success' ? <Check size={16} /> : <AlertCircle size={16} />} {message.text}
        </div>
      )}

      <div className="relative max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-boss-text-muted" />
        <input
          type="text" placeholder="Search by bill no or party..."
          value={search} onChange={e => setSearch(e.target.value)}
          className="w-full pl-9 pr-4 py-2 rounded-lg border border-boss-border text-sm focus:ring-2 focus:ring-boss-violet/30 focus:outline-none"
          data-testid="bilty-search"
        />
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-boss-border overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-boss-border bg-boss-surface-2">
              <th className="text-left py-3 px-4 font-medium text-boss-text-muted">Bill No.</th>
              <th className="text-left py-3 px-4 font-medium text-boss-text-muted">Date</th>
              <th className="text-left py-3 px-4 font-medium text-boss-text-muted">Party</th>
              <th className="text-left py-3 px-4 font-medium text-boss-text-muted">Transport</th>
              <th className="text-left py-3 px-4 font-medium text-boss-text-muted">Station</th>
              <th className="text-center py-3 px-4 font-medium text-boss-text-muted">Status</th>
              <th className="text-center py-3 px-4 font-medium text-boss-text-muted">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [...Array(5)].map((_, i) => <tr key={i}><td colSpan={7} className="py-3 px-4"><div className="h-4 bg-boss-surface-3 rounded animate-pulse" /></td></tr>)
            ) : invoices.map(inv => {
              const eligible = isBiltyEligible(inv);
              return (
                <tr key={inv._id} className="border-b border-boss-border/50 hover:bg-boss-surface-2/50">
                  <td className="py-2.5 px-4 font-mono text-xs">{inv.bill_no_full}</td>
                  <td className="py-2.5 px-4 text-boss-text-muted">{formatDate(inv.bill_date)}</td>
                  <td className="py-2.5 px-4 truncate max-w-[140px]">{inv.party_name}</td>
                  <td className="py-2.5 px-4">{inv.transport}</td>
                  <td className="py-2.5 px-4">{inv.station}</td>
                  <td className="py-2.5 px-4 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${getBiltyStatusColor(inv.bilty_status)}`}>
                      {inv.bilty_status || 'pending'}
                    </span>
                  </td>
                  <td className="py-2.5 px-4 text-center">
                    {eligible ? (
                      <label className="cursor-pointer">
                        <input
                          type="file" accept=".pdf" className="hidden"
                          onChange={e => { if (e.target.files[0]) handleUpload(inv._id, e.target.files[0], inv.bilty_status === 'uploaded'); }}
                          disabled={uploading === inv._id}
                        />
                        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                          inv.bilty_status === 'uploaded'
                            ? 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100'
                            : 'bg-boss-violet/10 text-boss-violet hover:bg-boss-violet/20'
                        }`}>
                          {uploading === inv._id ? <RefreshCw size={12} className="animate-spin" /> : inv.bilty_status === 'uploaded' ? <RefreshCw size={12} /> : <Upload size={12} />}
                          {inv.bilty_status === 'uploaded' ? 'Replace' : 'Upload'}
                        </span>
                      </label>
                    ) : (
                      <span className="text-xs text-boss-text-muted">N/A</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DocumentsSection() {
  const [docs, setDocs] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const load = () => {
    setLoading(true);
    getDocuments({ search }).then(r => { setDocs(r.data.documents || []); setTotal(r.data.total || 0); setLoading(false); }).catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, [search]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('doc_type', 'general');
      await uploadDocument(fd);
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Upload failed'); }
    setUploading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this document?')) return;
    try { await deleteDocument(id); load(); } catch (err) { alert('Delete failed'); }
  };

  const getFileIcon = (name) => {
    if (name?.endsWith('.pdf')) return <FileText size={16} className="text-red-500" />;
    if (name?.endsWith('.xlsx') || name?.endsWith('.xls')) return <FileText size={16} className="text-green-500" />;
    return <File size={16} className="text-gray-500" />;
  };

  return (
    <div className="space-y-4" data-testid="documents-page">
      <div className="flex items-center justify-between">
        <p className="text-boss-text-muted text-sm">{total} documents</p>
        <label className="flex items-center gap-2 px-4 py-2 bg-boss-violet text-white rounded-lg text-sm font-medium cursor-pointer hover:bg-boss-violet-2">
          <Upload size={16} /> {uploading ? 'Uploading...' : 'Upload Document'}
          <input type="file" className="hidden" onChange={handleUpload} disabled={uploading} data-testid="doc-upload-input" />
        </label>
      </div>

      <div className="relative max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-boss-text-muted" />
        <input type="text" placeholder="Search documents..." value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-9 pr-4 py-2 rounded-lg border border-boss-border text-sm focus:ring-2 focus:ring-boss-violet/30 focus:outline-none" />
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-boss-border overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="border-b bg-boss-surface-2">
            <th className="text-left py-3 px-4 font-medium text-boss-text-muted">File</th>
            <th className="text-left py-3 px-4 font-medium text-boss-text-muted">Type</th>
            <th className="text-left py-3 px-4 font-medium text-boss-text-muted">Size</th>
            <th className="text-left py-3 px-4 font-medium text-boss-text-muted">Uploaded</th>
            <th className="text-center py-3 px-4 font-medium text-boss-text-muted">Actions</th>
          </tr></thead>
          <tbody>
            {loading ? [...Array(3)].map((_, i) => <tr key={i}><td colSpan={5} className="py-3 px-4"><div className="h-4 bg-boss-surface-3 rounded animate-pulse" /></td></tr>) : docs.length === 0 ? (
              <tr><td colSpan={5} className="py-12 text-center text-boss-text-muted"><FolderOpen size={24} className="mx-auto mb-2 opacity-50" />No documents found</td></tr>
            ) : docs.map(d => (
              <tr key={d._id} className="border-b border-boss-border/50 hover:bg-boss-surface-2/50">
                <td className="py-2.5 px-4"><div className="flex items-center gap-2">{getFileIcon(d.file_name)}<span className="font-medium truncate max-w-[200px]">{d.file_name}</span></div></td>
                <td className="py-2.5 px-4"><span className="px-2 py-0.5 bg-boss-surface-3 rounded text-xs">{d.doc_type}</span></td>
                <td className="py-2.5 px-4 text-xs text-boss-text-muted">{d.file_size ? `${(d.file_size / 1024).toFixed(1)} KB` : '---'}</td>
                <td className="py-2.5 px-4 text-xs text-boss-text-muted">{formatDate(d.uploaded_at)}</td>
                <td className="py-2.5 px-4 text-center flex items-center justify-center gap-2">
                  {d.download_url && <a href={d.download_url} target="_blank" rel="noopener noreferrer" className="text-boss-violet hover:underline"><Download size={14} /></a>}
                  <button onClick={() => handleDelete(d._id)} className="text-red-500 hover:text-red-700"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function BiltyDocsPage() {
  const { isNavAllowed } = useAuth();
  // Bilty and Documents were separate features gated by separate RBAC
  // permissions (SCL_AGENT has scl/bilty but not dm/documents; SCRM_PARTY
  // is the reverse — an external party role that must not see internal
  // dispatch bilty tracking). Merging into one nav entry must not blur that
  // boundary, so each section keeps its own original permission check.
  const canBilty = isNavAllowed('/boss/scl/bilty');
  const canDocs = isNavAllowed('/boss/dm/documents');
  const [tab, setTab] = useState(canBilty ? 'bilty' : 'docs');

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-boss-text">Bilty Docs</h1>
        <p className="text-boss-text-muted text-sm">Dispatch bilty uploads and document center</p>
      </div>

      {canBilty && canDocs && (
        <div className="flex gap-1 border-b border-boss-border">
          <button
            onClick={() => setTab('bilty')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === 'bilty' ? 'border-boss-violet text-boss-violet' : 'border-transparent text-boss-text-muted hover:text-boss-text'}`}
          >
            Bilty Uploads
          </button>
          <button
            onClick={() => setTab('docs')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === 'docs' ? 'border-boss-violet text-boss-violet' : 'border-transparent text-boss-text-muted hover:text-boss-text'}`}
          >
            Documents
          </button>
        </div>
      )}

      {canBilty && (tab === 'bilty' || !canDocs) && <BiltySection />}
      {canDocs && (tab === 'docs' || !canBilty) && <DocumentsSection />}
    </div>
  );
}
